import { CommonModule, NgSwitch } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  FilterFormModel,
  FilterModel,
} from '../../../core/models/filters/filters.model';
import { RolesService } from '../../../core/services/user/roles.service';
import { UserRole } from '../../../../enums/roles.enum';
import {
  BsDatepickerConfig,
  BsDatepickerModule,
  BsLocaleService,
} from 'ngx-bootstrap/datepicker';
import { StatusUtils } from '../../../utils/status-utils';
import { DateFormatDirective } from '../../directives/date-format.directive';
import { CustomersService } from '../../../core/services/customer/customers.service';
import { RoleModel } from '../../../core/models/roles/roles.model';

@Component({
  selector: 'app-filters',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    BsDatepickerModule,
    NgSwitch,
    DateFormatDirective,
  ],
  templateUrl: './filters.component.html',
  styleUrl: './filters.component.scss',
})
export class FiltersComponent implements OnInit {
  constructor(
    private translate: TranslateService,
    private rolesService: RolesService,
    private bsLocaleService: BsLocaleService,
    private customersService: CustomersService
  ) {
    const userLang = localStorage.getItem('selectedLanguage') || 'en';
    this.translate.use(userLang);
    this.bsLocaleService.use(userLang);
  }
  @Input() filters: FilterModel[] = [];
  @Output() filtersChange = new EventEmitter<FilterModel[]>();

  isUserList: boolean = window.location.href.includes('/user/list');
  isAppointmentList: boolean = window.location.href.includes('appointment');
  //Role
  roles: RoleModel[] = [];
  userRole: any = UserRole;
  filterLabel: string = '';

  //Case Status
  caseStatuses = window.location.href.includes('appointment')
    ? StatusUtils?.getFilterWorkStatuses()
    : StatusUtils?.getFilterCaseStatuses();

  //Customer Code
  customerCodes: any[] = [];

  private statusOptions: { id: any; name: string }[] = null;
  private roleOptions: { id: any; name: string }[] = null;
  private customerCodeOptions: { id: any; name: string }[] = null;

  filterForm: FilterFormModel[] = [];
  maxDate = new Date();

  bsConfig: Partial<BsDatepickerConfig> = {
    isAnimated: true,
    dateInputFormat: 'DD-MM-YYYY',
    containerClass: 'theme-orange custom-datepicker',
  };
  async ngOnInit() {
    await this.getRoles();
    await this.getCustomerCodes();

    await StatusUtils?.initializeTranslateService(this.translate);
    this.caseStatuses = window.location.href.includes('appointment')
      ? StatusUtils?.getFilterWorkStatuses()
      : StatusUtils?.getFilterCaseStatuses();
  }

  getOptions(filterLabel: string): { id: any; name: string }[] {
    this.filterLabel = filterLabel;
    if (filterLabel === 'role') {
      if (!this.roleOptions && this.roles?.length > 0) {
        this.roleOptions = this.roles.map((role) => ({
          id: role.roleId,
          name: role.roleName,
        }));
      }
      return this.roleOptions || [];
    }

    if (filterLabel === 'status') {
      if (!this.statusOptions && this.caseStatuses?.length > 0) {
        this.statusOptions = this.caseStatuses.map((status) => ({
          id: status.id,
          name: status.title,
        }));
      }
      return this.statusOptions || [];
    }

    if (filterLabel === 'customerCode') {
      if (!this.customerCodeOptions && this.customerCodes?.length > 0) {
        this.customerCodeOptions = this.customerCodes.map((customerCode) => ({
          id: customerCode.code,
          name: customerCode.code,
        }));
      }
      return this.customerCodeOptions || [];
    }

    return [];
  }

  getFilterOptionName(option: { id: any; name: string }): string {
    if (this.filterLabel === 'status') {
      return StatusUtils?.getCaseStatusText(parseInt(option.id));
    }
    return option.name;
  }

  filterList(event: Event): void {
    event.preventDefault();
    this.filtersChange.emit(this.filters);
  }

  clearFilters(): void {
    this.filters.forEach((filter) => (filter.value = null));
    this.filtersChange.emit(this.filters);
  }

  async getRoles(): Promise<void> {
    return new Promise((resolve) => {
      this.rolesService.getRoles().subscribe({
        next: (res: any) => {
          this.roles = res.items
            .filter((role: any) => this.isValidRole(role))
            .map((role: any) => ({
              roleId: role.id,
              roleName: this.translate.instant(role.name),
              pages: role.rolePages.map((rp: any) => ({
                pageId: rp.pageId,
                pageName: rp.pageName,
                list: rp.list,
                add: rp.add,
                edit: rp.edit,
                detail: rp.detail,
                delete: rp.delete,
              })),
            }))
            .sort((a: RoleModel, b: RoleModel) =>
              a.roleName.localeCompare(b.roleName)
            );
          resolve();
        },
        error: () => {
          resolve();
        },
      });
    });
  }

  private isValidRole(role: { name: string }): boolean {
    const allowedRoles = [
      this.userRole.Admin,
      this.userRole.OrganizationAdmin,
      this.userRole.GeneralPractitioner,
      this.userRole.Dermatologist,
      this.userRole.Assistant,
    ];
    return allowedRoles.includes(role.name);
  }

  async getCustomerCodes(): Promise<void> {
    return new Promise((resolve) => {
      this.customersService.allCustomerCodes().subscribe({
        next: (res: any) => {
          this.customerCodes = res;
          resolve();
        },
      });
    });
  }
}
